import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useDelayGroupContext,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import React from 'react';

type Props = {
  posRef?: React.RefObject<HTMLElement>;
  initialOpen?: boolean;
  placement?: Placement;
  showArrow?: boolean;
  mainAxisOffset?: number;
};

const useTooltip = ({
  initialOpen = false,
  placement = 'top',
  mainAxisOffset = 0,
  showArrow = true,
  ...props
}: Props) => {
  const arrowRef = React.useRef(null);
  const [open, setOpen] = React.useState(initialOpen);
  const { delay } = useDelayGroupContext();

  const floatingData = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: mainAxisOffset,
      }),
      flip({
        padding: 10,
      }),
      shift({
        padding: 10,
      }),
      arrow({ element: arrowRef, padding: 5 }),
    ],
    strategy: 'fixed',
  });

  const hover = useHover(floatingData.context, {
    move: false,
    delay,
  });
  const focus = useFocus(floatingData.context, {});
  const dismiss = useDismiss(floatingData.context, {});
  const role = useRole(floatingData.context, { role: 'tooltip' });
  const interactions = useInteractions([hover, focus, dismiss, role]);

  React.useEffect(() => {
    if (props.posRef) {
      floatingData.refs.setPositionReference(props.posRef.current);
    }
  }, [floatingData.refs, props.posRef]);

  return React.useMemo(
    () => ({
      arrowRef,
      open,
      setOpen,
      showArrow,
      ...interactions,
      ...floatingData,
    }),
    [open, setOpen, showArrow, interactions, floatingData],
  );
};

export { useTooltip, Props as UseTooltipProps };
